import queryString from 'query-string';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import FaqForm from './components/FaqForm';
import FaqsNavBar from './components/FaqsNavBar';
import PinsAndPremiumPack from './components/PinsAndPremiumPack';
import RegistrationAndLogin from './components/RegistrationAndLogin';
import Scores from './components/Scores';
import { SideBar, SidebarProps } from './components/SideBar';
import SpecialTournaments from './components/SpecialTournaments';
import TeamBuildingAndAwards from './components/TeamBuildingAndAwards';

import { NavBar } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useUndepsHostbackButton } from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePermutive } from '@/hooks/usePermutive';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { SelectedScreen } from '@/types/editProfile';
import { letterGenderById } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface LocationStateProps extends SelectedScreen {
  [key: string]: any; // Para permitir otras propiedades que puedan venir en el state
}

export const Faqs = () => {
  const { userInitialData: userLogged } = useAuth();
  const { isMobile, isDesktop } = useIsDesktop();
  const location = useLocation(); // Renombramos para claridad

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.faqs,
        kw_premium: userLogged?.premium,
        kw_edad: userLogged?.edad_dt,
        kw_sexo: letterGenderById(userLogged?.idSexo),
        kw_equipo: userLogged?.club_hincha,
        kw_fecha: userLogged?.fecha,
        kw_pines: userLogged?.pinesDisponibles,
        kw_saldo: userLogged?.saldo,
        kw_puntosporfecha: userLogged?.puntos_fecha,
        kw_puntosportorneo: userLogged?.puntos_general,
        kw_posicionporfecha: userLogged?.posicion_fecha,
        kw_posicionportorneo: userLogged?.posicion_general,
        kw_recibirinfo: userLogged?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  const parsed = queryString.parse(location.search);
  const originalLocationState = location.state
    ? (location.state as LocationStateProps)
    : {
        selectedScreen: parsed?.selectedScreen,
        accordionId: parsed?.accordionId,
      }; // Casteamos el tipo

  // Obtener la sección de FAQ desde localStorage si existe
  const storedSection = localStorage.getItem('faqSection');
  const storedAccordionId = localStorage.getItem('faqAccordionId');

  const [controlledLocationState, setControlledLocationState] = useState<
    LocationStateProps | undefined
  >(() => {
    if (storedSection || storedAccordionId) {
      // Prioridad a localStorage
      return {
        selectedScreen: storedSection ?? originalLocationState?.selectedScreen,
        accordionId: storedAccordionId ?? originalLocationState?.accordionId,
      };
    }
    // Si no hay localStorage, usamos el estado de useLocation
    return originalLocationState;
  });

  // Asegura que el scroll esté en la parte superior cuando se monta el componente
  useScrollToTop();

  const Screens: Record<Exclude<SidebarProps['selected'], ''>, ReactNode> = {
    RegistrationAndLogin: (
      <RegistrationAndLogin
        isPublic={!userLogged}
        state={controlledLocationState}
        title={'Registro e inicio de sesión'}
      />
    ),
    TeamBuildingAndAwards: (
      <TeamBuildingAndAwards
        state={controlledLocationState}
        title={'Armado de equipos y premios'}
      />
    ),
    PinsAndPremiumPack: (
      <PinsAndPremiumPack
        isPublic={!userLogged}
        state={controlledLocationState}
        title={'Pines y Pack Premium'}
      />
    ),
    Scores: <Scores state={controlledLocationState} title={'Puntajes'} />,
    SpecialTournaments: (
      <SpecialTournaments
        state={controlledLocationState}
        title={'Torneos especiales'}
      />
    ),
    FaqForm: <FaqForm isPublic={!userLogged} />,
  };

  const [screen, setScreen] = useState<keyof typeof Screens | ''>(
    ((storedSection as keyof typeof Screens) ||
      originalLocationState?.selectedScreen) ??
      ''
  );

  useEffect(() => {
    if (controlledLocationState?.accordionId) {
      localStorage.removeItem('faqSection');
      localStorage.removeItem('faqAccordionId');

      // Después de que el render inicial (con el accordionId) ha ocurrido,
      // actualizamos el estado para eliminar el accordionId.
      // Esto causará un re-render, pero esta vez sin el accordionId.
      setControlledLocationState((prevState) => {
        if (!prevState) return undefined; // En caso de que ya sea undefined
        const newState = { ...prevState };
        delete newState.accordionId; // Eliminamos solo el accordionId
        return newState;
      });
    }
  }, [controlledLocationState?.accordionId]); // Dependencia clave: solo se dispara cuando accordionId cambia (de null/undefined a un valor, o viceversa)

  const handleBackAction = () => {
    setScreen('');
  };

  useUndepsHostbackButton(() => {
    setScreen('');
  });

  const getActionObject = () => {
    if (screen === '' || !isMobile) {
      return {
        showMenuAction: true,
        items: true,
      };
    } else {
      return {
        backAction: handleBackAction,
      };
    }
  };

  // Contenido común que se renderizará dentro del NavBar o FaqsNavBar
  const content = (
    <div
      className={`grid grid-cols-12 min-h-screen ${
        isMobile ? 'overflow-auto pb-10' : ''
      }`}
    >
      <div
        className={`col-span-12 xl:col-span-4 2xl:col-span-3 ${
          isMobile && screen !== '' ? 'h-full overflow-auto' : ''
        }`}
      >
        {screen === '' || !isMobile ? (
          <SideBar
            selected={screen}
            onSelect={(selected) => setScreen(selected)}
          />
        ) : (
          <div className="overflow-auto">{Screens[screen]}</div>
        )}
      </div>
      <div className="col-span-8 xl:col-span-8 2xl:col-span-9 max-md:hidden">
        {screen === '' ? Screens.RegistrationAndLogin : Screens[screen]}
        {isDesktop && (
          <>
            <FaqForm isPublic={!userLogged} />
          </>
        )}
      </div>
    </div>
  );

  // Props comunes para ambos componentes de navegación
  const navProps = {
    desktop: true,
    mobile: {
      leftContent: getActionObject(),
      centerContent: {
        title: screen === 'FaqForm' ? 'Contactanos' : 'Preguntas Frecuentes',
      },
    },
  };

  return (
    <>
      {!userLogged || !userLogged.tiene_equipo ? (
        <FaqsNavBar {...navProps} userLogged={userLogged}>
          {content}
        </FaqsNavBar>
      ) : (
        <NavBar {...navProps}>{content}</NavBar>
      )}
    </>
  );
};
